/**
 * @file Team photo product filtering utils.
 */

/**
 * Whether a product/size name identifies it as a Team product.
 *
 * @param {string} name - The name to check.
 * @returns {boolean} - Whether the name is a Team one.
 */
const isTeamName = name => (name ?? '').toLowerCase().includes('team');

/**
 * Whether a product/size name identifies it as a Memory Mate.
 *
 * @param {string} name - The name to check.
 * @returns {boolean} - Whether the name is a Memory Mate one.
 */
const isMemoryMateName = name =>
  (name ?? '').toLowerCase().includes('memory mate');

/**
 * Whether a custom print name identifies it as a team photo one.
 *
 * @param {string} name - The name to check.
 * @returns {boolean} - Whether the custom print is a team photo one.
 */
const isTeamCustomPrintName = name =>
  isTeamName(name) || isMemoryMateName(name);

/**
 * Builds the filter deciding whether a photo pricing item (print items, print
 * products) is purchasable for a photo.
 *
 * Team photos are limited to Team items (and Team items are hidden from
 * individual photos). When the pro has not configured any Team item though,
 * that limit would leave the team photo with no print to purchase, so we fall
 * back to allowing every one of them on it.
 *
 * @param {Object} params - The params.
 * @param {boolean} params.isTeamPhoto - Whether the photo is a team photo.
 * @param {boolean} params.hasTeamPhotoPricings - Whether any Team photo pricing
 * item is available.
 * @returns {Function} - Filter receiving a product/size name.
 */
const createTeamPhotoPricingFilter =
  ({ isTeamPhoto, hasTeamPhotoPricings }) =>
  name => {
    if (!isTeamPhoto) {
      return !isTeamName(name);
    }

    return hasTeamPhotoPricings ? isTeamName(name) : true;
  };

/**
 * Builds the filter deciding whether a custom print is purchasable for a photo.
 *
 * Works like the photo pricing one, independently from it, except team photos
 * take both Team and Memory Mate custom prints.
 *
 * @param {Object} params - The params.
 * @param {boolean} params.isTeamPhoto - Whether the photo is a team photo.
 * @param {boolean} params.hasTeamCustomPrints - Whether any Team/Memory Mate
 * custom print is available.
 * @returns {Function} - Filter receiving a custom print name.
 */
const createTeamCustomPrintFilter =
  ({ isTeamPhoto, hasTeamCustomPrints }) =>
  name => {
    if (!isTeamPhoto) {
      return !isTeamName(name);
    }

    return hasTeamCustomPrints ? isTeamCustomPrintName(name) : true;
  };

export {
  isTeamName,
  isMemoryMateName,
  isTeamCustomPrintName,
  createTeamPhotoPricingFilter,
  createTeamCustomPrintFilter,
};
